import React, { useState, useEffect } from "react";
import { Title, CreateRoomContainer, InputContainer, InputWrap } from "./CreateRoom.styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setTotalRounds, setChosenTeam, clearStimulyatorData } from '../../../app/features/stimulyatorDraftRoom/stimulyatorDraftRoomSlice';
import { selectUser } from "../../../app/features/user/userSlice";
import { toast } from "react-toastify";
import { API_ENDPOINT } from "../../../config/config";
import axiosInstance from "../../../service/axiosInstance";

const CreateRoom = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { userInfo } = useSelector(selectUser);


    const ROOM_TYPE_MAP = {
        public: "PUBLIC",
        private: "PRIVATE",
    };

    const [options, setOptions] = useState({
        room_type: "",
        team: "",
        rounds: null,
        multi_day: false,
        allow_trades: null,
        picking_time: null,
        draft_length: "",
        round_day_1: "",
        allow_trades_day_1: null,
        picking_time_day_1: null,
        round_day_2: "",
        allow_trades_day_2: null,
        picking_time_day_2: null,
        round_day_3: "",
        allow_trades_day_3: null,
        picking_time_day_3: null,
    });
    const [selected_date_time, setSelectedDateTime] = useState(null);
    const [loading, setLoading] = useState(false);
    const [id, setId]=useState(null);
    const [orderedTeams, setOrderedTeams] = useState([]);
    const [simulatorOrder, setSimulatorOrder] = useState([]);
    const [teamsLoading, setTeamsLoading] = useState(false);


    useEffect(() => {
        dispatch(clearStimulyatorData());
    }, [dispatch]);

    useEffect(() => {
        if (!options.rounds) return;
        let isMounted = true;
        const fetchTeams = async () => {
            setTeamsLoading(true);
            try {
                const roundCount = Number(options.rounds);
                const response = await axiosInstance.get(
                    `${API_ENDPOINT}trade-value-history/?limit=1000&offset=0&round=&round_index_number=${roundCount}&tm=`
                );
                const teams = response?.data?.results || [];
                if (isMounted) {
                    setSimulatorOrder(teams);
                }
                const teamSet = [];
                teams.forEach((team) => {
                    const teamRound = team.round;
                    const exists = teamSet.some((entry) => entry.name === teamRound.name);
                    if (!exists) {
                        teamSet.push({
                            id: teamRound.id,
                            name: teamRound.name,
                            logo: teamRound.logo,
                            selection: team.index,
                            draftCountId: team.team_neads_info.id,
                        });
                    }
                });
                const sortedTeams = teamSet.sort(
                    (a, b) => (a.selection ?? 0) - (b.selection ?? 0)
                );
                if (isMounted) {
                    setOrderedTeams(sortedTeams);
                    if (options.team && !sortedTeams.some((team) => team.name === options.team)) {
                        setOptions((prev) => ({ ...prev, team: "" }));
                        dispatch(setChosenTeam(""));
                    }
                }
            } catch (error) {
                if (isMounted) {
                    setOrderedTeams([]);
                    setSimulatorOrder([]);
                    if (options.team) {
                        setOptions((prev) => ({ ...prev, team: "" }));
                        dispatch(setChosenTeam(""));
                    }
                }
            } finally {
                if (isMounted) {
                    setTeamsLoading(false);
                }
            }
        };
        fetchTeams();
        return () => {
            isMounted = false;
        };
    }, [options.rounds]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let val;
        if (type === "checkbox") {
            val = checked;
        } else if (name === "room_type") {
            val = value.toLowerCase();
        } else if (type === "radio" && (value === "true" || value === "false")) {
            val = value === "true";
        } else if (name?.includes("round") || name === "rounds" || name?.includes("picking_time")) {
            val = value === "" ? "" : Number(value);
        } else {
            val = value;
        }
        setOptions((prev) => ({ ...prev, [name]: val }));
        if (name === "rounds") dispatch(setTotalRounds(Number(val)));
        if (name === "team") dispatch(setChosenTeam(val));
    };

    const handleSelectTeam = (teamName) => {
        setOptions((prev) => ({ ...prev, team: teamName }));
        dispatch(setChosenTeam(teamName));
    };


    const isFormValid = () => {
        if (
            !options.room_type ||
            !options.team ||
            options.rounds === null ||
            selected_date_time === null || selected_date_time === ""
        ) {
            return false;
        }
        if (!options.multi_day) {
            if (
                options.allow_trades === null ||
                !options.picking_time
            ) return false;
        }
        if (options.multi_day) {
            if (!options.draft_length) return false;
            if (
                !options.round_day_1 ||
                options.allow_trades_day_1 === null ||
                !options.picking_time_day_1
            ) return false;
            if (options.draft_length >= "2") {
                if (
                    !options.round_day_2 ||
                    options.allow_trades_day_2 === null ||
                    !options.picking_time_day_2
                ) return false;
            }
            if (options.draft_length === "3") {
                if (
                    !options.round_day_3 ||
                    options.allow_trades_day_3 === null ||
                    !options.picking_time_day_3
                ) return false;
            }
        }
        return true;
    };


    const getRoundOptions = (day) => {
        const draftLength = Number(options.draft_length) || 0;
        const total = Number(options.rounds) || 0;
        const d1 = Number(options.round_day_1) || 0;
        const d2 = Number(options.round_day_2) || 0;
        if (!total || draftLength < 1 || total < draftLength) return [];
        const makeRange = (n) => (n > 0 ? Array.from({ length: n }, (_, i) => i + 1) : []);
        if (draftLength === 2) {
            if (day === 1) {
                const maxForDay1 = total - 1;
                return makeRange(maxForDay1);
            }
            if (day === 2) {
                if (!d1) return [];
                const remaining = total - d1;
                return remaining > 0 ? [remaining] : [];
            }
        }
        if (draftLength === 3) {
            if (day === 1) {
                const maxForDay1 = total - 2;
                return makeRange(maxForDay1);
            }
            if (day === 2) {
                if (!d1) return [];
                const maxForDay2 = total - d1 - 1;
                return makeRange(Math.max(0, maxForDay2));
            }
            if (day === 3) {
                if (!d1 || !d2) return [];
                const remaining = total - d1 - d2;
                return remaining > 0 ? [remaining] : [];
            }
        }
        return [];
    };

    useEffect(() => {
        setOptions(prev => {
            const next = { ...prev };
            const total = Number(next.rounds) || 0;
            const draftLength = Number(next.draft_length) || 0;
            if (!total || draftLength < 1 || total < draftLength) {
                next.round_day_1 = "";
                next.round_day_2 = "";
                next.round_day_3 = "";
                return next;
            }
            const maxDay1 = total - (draftLength - 1);
            if (next.round_day_1 && Number(next.round_day_1) > maxDay1) {
                next.round_day_1 = "";
                next.round_day_2 = "";
                next.round_day_3 = "";
                return next;
            }
            if (next.round_day_1) {
                const maxDay2 = draftLength === 3 ? total - Number(next.round_day_1) - 1 : total - Number(next.round_day_1);
                if (next.round_day_2 && Number(next.round_day_2) > Math.max(0, maxDay2)) {
                    next.round_day_2 = "";
                    next.round_day_3 = "";
                    return next;
                }
            }
            return next;
        });
    }, [options.rounds, options.draft_length]);


    useEffect(() => {
        if (Number(options.draft_length) === 3 && options.round_day_1 && options.round_day_2) {
            const total = Number(options.rounds) || 0;
            const d1 = Number(options.round_day_1) || 0;
            const d2 = Number(options.round_day_2) || 0;
            const rest = total - d1 - d2;
            setOptions(prev => ({ ...prev, round_day_3: rest > 0 ? rest : "" }));
        } else {
            setOptions(prev => ({ ...prev, round_day_3: prev.round_day_3 ? "" : prev.round_day_3 }));
        }
    }, [options.round_day_1, options.round_day_2, options.draft_length, options.rounds]);


    const cleanData = (obj) => {
        const result = {};
        Object.entries(obj).forEach(([key, value]) => {
            if (value !== "" && value !== null && value !== undefined) {
                result[key] = value;
            }
        });
        return result;
    };


    const handleSaveAndPublish = async () => {
        if (!isFormValid()) {
            toast.error("Please fill in all required fields before saving!");
            return;
        }
        setLoading(true);
        try {
            const getUTCFromLocal = (localStr) => {
                if (!localStr) return null;
                const [date, time] = localStr.split("T");
                const [year, month, day] = date.split("-").map(Number);
                const [hours, minutes] = time.split(":").map(Number);
                const localDate = new Date(year, month - 1, day, hours, minutes);
                return localDate.toISOString();
            };
            const formattedData = cleanData({
                ...options,
                room_type: ROOM_TYPE_MAP[options.room_type] || null,
                selected_date_time: getUTCFromLocal(selected_date_time),
            });
            const response = await axiosInstance.post(`${API_ENDPOINT}event-config/`, formattedData);
            const eventId = response.data.id;
            if (eventId) {
                const slotPayload = orderedTeams.map((team, index) => ({
                    slot: index + 1,
                    team_id: team.id,
                    team_name: team.name,
                    user_id: options.team === team.name ? (userInfo?.id ?? null) : null,
                }));

                console.log(slotPayload);

                await axiosInstance.post(
                    `${API_ENDPOINT}event-config/${eventId}/slots/`,
                    slotPayload
                );

                const roundCount = Number(options.rounds) || 1;
                const picksSource = simulatorOrder
                    .filter((item) => Number(item.round_index_number) <= roundCount)
                    .slice()
                    .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
                if (!picksSource.length) {
                    toast.error("Draft order not loaded. Please select rounds and try again.");
                    return;
                }
                const picksPayload = picksSource.map((item) => ({
                    index: item?.index,
                    pick: item?.pick,
                    round_index_number: item?.round_index_number,
                    value: item?.value ?? null,
                    switching: item?.switching ?? null,
                    team_id: item?.round?.id,
                    team_name: item?.round?.name,
                })).filter((item) => item.team_id && item.team_name);

                console.log("picksPayload", picksPayload);

                try {
                    await axiosInstance.post(
                        `${API_ENDPOINT}event-config/${eventId}/picks/`,
                        picksPayload
                    );
                } catch (error) {
                    console.error("Error creating draft picks", error);
                    toast.error("Error creating draft order. Please try again.");
                    return;
                }
            }
            setId(eventId);
            toast.success("Room saved successfully!");
            setSelectedDateTime(null);
            setOptions({
                room_type: "",
                team: "",
                rounds: null,
                multi_day: false,
                allow_trades: null,
                picking_time: null,
                draft_length: "",
                round_day_1: "",
                allow_trades_day_1: null,
                picking_time_day_1: null,
                round_day_2: "",
                allow_trades_day_2: null,
                picking_time_day_2: null,
                round_day_3: "",
                allow_trades_day_3: null,
                picking_time_day_3: null,
            });
        } catch (err) {
            toast.error("Error saving room!");
        } finally {
            setLoading(false);
        }
    };


    const renderRoundDays = () => {
        const numDays = Number(options.draft_length);
        if (!numDays) return null;
        return Array.from({ length: numDays }, (_, i) => {
            const day = i + 1;
            const roundOptions = getRoundOptions(day);
            const isDisabled = (day === 2 && !options.round_day_1) || (day === 3 && (!options.round_day_1 || !options.round_day_2));
            return (
                <div key={day} className="fieldGroup" style={{ marginTop: "20px" }}>
                    <label>Round Day {day}</label>
                    <select
                        name={`round_day_${day}`}
                        value={options[`round_day_${day}`] || ""}
                        onChange={handleChange}
                        disabled={isDisabled}
                    >
                        <option value="">Select round</option>
                        {roundOptions.map((r) => (
                            <option key={r} value={r}>
                                Round {r}
                            </option>
                        ))}
                    </select>
                    <div className="fieldGroup" style={{ marginTop: "10px" }}>
                        <label>Allow Trades {day}</label>
                        <div className="checkboxRow">
                            <div className="checkboxPart">
                                <label>
                                    <input
                                        type="radio"
                                        name={`allow_trades_day_${day}`}
                                        value="true"
                                        checked={options[`allow_trades_day_${day}`] === true}
                                        onChange={handleChange}
                                        disabled={isDisabled}
                                    />
                                    Yes
                                </label>
                            </div>
                            <div className="checkboxPart">
                                <label>
                                    <input
                                        type="radio"
                                        name={`allow_trades_day_${day}`}
                                        value="false"
                                        checked={options[`allow_trades_day_${day}`] === false}
                                        onChange={handleChange}
                                        disabled={isDisabled}
                                    />
                                    No
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className="fieldGroup" style={{ marginTop: "10px" }}>
                        <label>Picking Time {day}</label>
                        <select
                            name={`picking_time_day_${day}`}
                            value={options[`picking_time_day_${day}`] || ""}
                            onChange={handleChange}
                            disabled={isDisabled}
                            style={{ height: "36px" }}
                        >
                            <option value="">Select time</option>
                            {times.map((time) => (
                                <option key={time} value={time}>
                                    {time}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            );
        });
    };

    const handleDateChange = (dayjsValue) => {
        setSelectedDateTime(dayjsValue ? dayjsValue.format("YYYY-MM-DDTHH:mm") : null);
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const opts = {
            weekday: "short",
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            timeZoneName: "short",
        };
        return new Intl.DateTimeFormat("en-US", opts)
            .format(date)
            .replace(",", "")
            .replace(/\//g, "-")
            .replace("AM", "am")
            .replace("PM", "pm");
    };

    const times = Array.from({ length: 10 }, (_, i) => i + 1);


    return (
        <>
            <Title>Create Room</Title>
            <CreateRoomContainer>
                <div className={"block"}>
                    <div className={"subtitle"}>Basic Options</div>
                    <div className={"fieldGroup"}><label>Room type</label>
                        <div className={"checkboxRow"}>
                            <div className={"checkboxPart"}>
                                <label>
                                    <input
                                        type="radio"
                                        name="room_type"
                                        value="public"
                                        checked={options.room_type === "public"}
                                        onChange={handleChange}
                                    />
                                    Public
                                </label>
                            </div>
                            <div className={"checkboxPart"}>
                                <label>
                                    <input
                                        type="radio"
                                        name="room_type"
                                        value="private"
                                        checked={options.room_type === "private"}
                                        onChange={handleChange}
                                    />
                                    Private
                                </label>
                            </div>
                        </div>
                    </div>
                    <p style={{ marginBottom: "20px" }}>
                        *Public vs Private: Public rooms will be listed in the lobby for any user to join. Private
                        rooms are not listed in the lobby but can be joined if they have the link.
                    </p>
                    <div className={"fieldGroup"}>
                        <label># of Rounds</label>
                        <select name="rounds" value={options.rounds || ""} onChange={handleChange}>
                            <option value="">Select round</option>
                            {[1, 2, 3, 4, 5, 6, 7].map((r) => (
                                <option key={r} value={r}>
                                    Round {r}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={"checkboxField"}>
                        <span>Multi Day Event</span>
                        <input
                            type="checkbox"
                            name="multi_day"
                            checked={options.multi_day}
                            onChange={handleChange}
                        />
                    </div>
                    {options.multi_day && (
                        <div className="fieldGroup">
                            <label>Draft Length (days)</label>
                            <select
                                name="draft_length"
                                value={options.draft_length || ""}
                                onChange={handleChange}
                            >
                                <option value="">Select length</option>
                                <option value="2">2</option>
                                <option value="3">3</option>
                            </select>
                        </div>
                    )}
                    {options.multi_day && renderRoundDays()}
                    {!options.multi_day &&
                        <div className={"fieldGroup"}>
                            <label>Allow Trades</label>
                            <div className={"checkboxRow"}>
                                <div className={"checkboxPart"}>
                                    <label>
                                        <input
                                            type="radio"
                                            name="allow_trades"
                                            value="true"
                                            checked={options.allow_trades === true}
                                            onChange={handleChange}
                                        />
                                        Yes
                                    </label>
                                </div>
                                <div className={"checkboxPart"}>
                                    <label>
                                        <input
                                            type="radio"
                                            name="allow_trades"
                                            value="false"
                                            checked={options.allow_trades === false}
                                            onChange={handleChange}
                                        />
                                        No
                                    </label>
                                </div>
                            </div>
                        </div>
                    }
                    {!options.multi_day &&
                        <div className="fieldGroup">
                            <label>Picking Time</label>
                            <select
                                name="picking_time"
                                value={options.picking_time || ""}
                                onChange={handleChange}
                                style={{ height: "36px" }}
                            >
                                <option value="">Select time</option>
                                {times.map((time) => (
                                    <option key={time} value={time}>
                                        {time}
                                    </option>
                                ))}
                            </select>
                        </div>
                    }
                    <div className={"fieldGroup"}>
                        <div className={"subtitle"}>Start Time</div>
                        <p>
                            These rooms will not fill as quickly as fantasy football type mock drafts.
                            The farther out your start date/time, the more likely you are to get participants.
                        </p>
                        <InputContainer>
                            <label>Date</label>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker
                                    value={selected_date_time ? dayjs(selected_date_time) : null}
                                    onChange={handleDateChange}
                                    minDateTime={dayjs()}
                                    maxDateTime={dayjs().add(84, "day")}
                                    slotProps={{ textField: { fullWidth: true, size: "small" } }}
                                />
                            </LocalizationProvider>
                        </InputContainer>
                        <InputContainer>
                            <label style={{ marginBottom: "10px" }}>Selected start date/time</label>
                            <p>{formatDateTime(selected_date_time)}</p>
                        </InputContainer>
                    </div>
                </div>
                <div className={"block"}>
                    <div className={"fieldGroup"}>
                        <div className={"subtitle"}>Select Your Team</div>
                        {teamsLoading && (
                            <div className="team-loading">Loading teams...</div>
                        )}
                        {!teamsLoading && (
                            <table className="team-table">
                                <thead>
                                <tr>
                                    <th>#</th>
                                    <th>TEAM</th>
                                    <th></th>
                                </tr>
                                </thead>
                                <tbody>
                                {orderedTeams.map((team, index) => {
                                    const isSelected = options.team === team.name;
                                    const assignedLabel = "You";
                                    return (
                                        <tr
                                            key={team.id || team.name}
                                            className={`team-row${isSelected ? " selected" : ""}`}
                                            onClick={() => handleSelectTeam(team.name)}
                                        >
                                            <td>{index + 1}</td>
                                            <td className="team-cell">
                                                {team.logo ? (
                                                    <img
                                                        className="team-logo"
                                                        src={team.logo}
                                                        alt={`${team.name} logo`}
                                                    />
                                                ) : null}
                                                <span>{team.name}</span>
                                            </td>
                                            <td className="slot">
                                                {isSelected ? assignedLabel : "Slot Available"}
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
                <div className={"block"}>
                    <div className={"subtitle"}>Save Draftroom</div>
                    <p>
                        <b>Room type:</b> {options.room_type}
                    </p>
                    <p>
                        <b>Your team:</b> {options.team}
                    </p>
                    <p>
                        <b># of rounds:</b> {options.rounds}
                    </p>
                    <p>
                        <b>Start time:</b> {formatDateTime(selected_date_time)}
                    </p>
                    {!options.multi_day && (
                        <p>
                            <b>Allow trades:</b> {options.allow_trades === true ? "Yes" : options.allow_trades === false ? "No" : ""}
                        </p>
                    )}
                    {!options.multi_day &&
                        <p>
                            <b>Time between picks:</b> {options.picking_time}
                        </p>
                    }
                    {options.multi_day &&
                        <>
                            <p>
                                <b>Allow trades 1:</b> {options.allow_trades_day_1 ? "Yes" : "No"}
                            </p>
                            <p>
                                <b>Time between picks 1:</b> {options.picking_time_day_1}min
                            </p>
                            <p>
                                <b>Allow trades 2:</b> {options.allow_trades_day_2 ? "Yes" : "No"}
                            </p>
                            <p>
                                <b>Time between picks 2:</b> {options.picking_time_day_2}min
                            </p>
                            {options.draft_length ==="3" &&
                                <>
                                    <p>
                                        <b>Allow trades 3:</b>  {options.allow_trades_day_3 ? "Yes" : "No"}
                                    </p>
                                    <p>
                                        <b>Time between picks 3:</b> {options.picking_time_day_3}min
                                    </p>
                                </>
                            }
                        </>
                    }
                    <div className={"buttonsContainer"}>
                        <button onClick={handleSaveAndPublish} disabled={loading}>
                            {loading ? "Saving..." : "Save & Publish"}
                        </button>
                        {id && (
                            <button
                                onClick={() => navigate(`/waiting-room/${id}`)}
                            >
                                Go To Room
                            </button>
                        )}
                    </div>
                </div>
            </CreateRoomContainer>
        </>
    );
};

export default CreateRoom;

